/**
 * KATALOG SAĞLIĞI — TEK KURAL LİSTESİ (Genişleme Adım 3).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 KURAL LİSTESİ BURADA VE YALNIZ BURADA
 * ═══════════════════════════════════════════════════════════════════════════
 * Puanı hesaplayan, hangi maddenin ihlal edildiğini söyleyen, AI düzeltmesinin
 * neyi düzeltebileceğine karar veren ve arayüzün rozet bastığı liste AYNI
 * liste. İkinci bir kopya çıkarsa ("UI'da kısa bir özet listesi") ikisi kaçınılmaz
 * olarak ayrışır ve kullanıcı, panelde gördüğü maddeyle puanın uyuşmadığı bir
 * ekranla kalır.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * PUAN: DOĞRUDAN CEZA — normalize oran DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 *     puan = max(0, 100 − ihlal edilen kuralların ağırlık toplamı)
 *
 * `ağırlık` böylece "bu madde kaç puana mal oluyor" demek — kullanıcıya
 * açıklanabilir tek biçim bu.
 *
 * REDDEDİLEN alternatif: `100 × (1 − ihlal/toplam)`. Toplam ağırlık 164 ve o
 * formülde görselı HİÇ OLMAYAN bir ürün 82 puan alırdı — yani "neredeyse
 * mükemmel" görünürdü. Oysa görselsiz ürün reklama GİREMİYOR. Ceza biçiminde
 * aynı ürün 70 alıyor ve üst üste binen kusurlar puanı gerçekten dibe çekiyor
 * (görselsiz + stoksuz + açıklamasız + fiyatsız = 23).
 *
 * Toplamın 100'ü aşması (164) bilinçli: her kuralı birden ihlal eden ürün 0'da
 * dipleniyor ve "bundan daha kötüsü var" ayrımı zaten anlamsız.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `imageCount === 0` GERÇEKTEN "hiç görseli yok" DEMEK
 * ═══════════════════════════════════════════════════════════════════════════
 * Alan Shopify'ın `mediaCount`undan geliyor ve videolu üründe kapak karesi de
 * sayılıyor. Yani "belki videosu vardır" mazereti YOK. `NO_IMAGE` bu yüzden
 * listenin tek başına en ağır maddesi (30) ve ikincinin (18) belirgin üstünde.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu liste nasıl üretildi
 * ═══════════════════════════════════════════════════════════════════════════
 * Beş bağımsız mercek (görsel · metin · sınıflandırma · ticari hazırlık · feed
 * uygunluğu) 30 kural önerdi; her kural ayrı bir ajan tarafından DÜŞMANCA
 * denetlendi (hesaplanabilirlik / yanlış pozitif / ağırlık / AI düzeltilebilir
 * mi) ve bir sentez adımı çakışanları birleştirip ağırlıkları dengeledi.
 * 2 kural düştü, 12'si denetimde DÜZELTİLDİ. Denetimin yakaladığı gerçek
 * hatalardan bazıları — hepsi bu dosyaya yansımış durumda:
 *
 *   · `OUT_OF_STOCK` → `[].every()` `true` döndüğü için varyantı henüz
 *     senkronlanmamış HER ürün "tükendi" sayılıyordu (`vs.length > 0` eklendi)
 *   · `ZERO_PRICE` → `some` ile yazılmıştı; ücretsiz numune varyantı olan
 *     sağlıklı ürünleri de vuruyordu
 *   · `TITLE_TOO_SHORT` → "Sony WH-1000XM5" ve "Beymen Cüzdan" gibi DOĞRU
 *     başlıkları vuruyordu (marka + model kodu muafiyeti eklendi)
 *   · `DESCRIPTION_TOO_SHORT` → 150 karakter eşiği belgeye dayanmıyordu;
 *     ne Merchant Center'ın ne Meta'nın asgari uzunluk şartı var → 80
 *   · `FEED_IDENTIFIER_MISSING` → yalnız SKU'ya bakıyordu ve kimliği barkod
 *     üzerinden yürüyen katalogları (Trendyol'da barkod birincil kimlik)
 *     toptan ihlalli işaretliyordu
 *   · `TITLE_SPAM_SIGNALS` → süs sembolü sayacı `u` bayrağı olmadan tek bir
 *     🔥'i iki karakter sayıp kendi başına eşiği geçiriyordu
 *
 * ⚠️ Kod (`code`) alanları VERİTABANINA yazılıyor (`products.healthIssues`).
 * Yeniden adlandırmak, geçmiş satırların anlamını sessizce kaybettirir.
 */

export type CatalogHealthSeverity = 'critical' | 'high' | 'medium' | 'low';

/// AI düzeltmesinin yazabileceği alanlar. 🔴 Liste KAPALI: görsel çekmek,
/// stok doldurmak ve fiyat değiştirmek AI'ın işi değil.
export type CatalogFixableField = 'title' | 'description' | 'productType' | 'tags';

/// Kuralın gördüğü ürün — `Product` satırının kural için gereken kadarı.
export interface CatalogHealthProduct {
  title: string;
  description: string | null;
  handle: string | null;
  productType: string | null;
  vendor: string | null;
  tags: string[];
  status: string;
  imageUrl: string | null;
  imageCount: number;
  onlineStoreUrl: string | null;
}

/// Kuralın gördüğü varyant. `price` Prisma'da `Decimal` — kurallar `Number()`
/// ile okuyor, düz karşılaştırma sessizce yanlış sonuç veriyor.
export interface CatalogHealthVariant {
  sku: string | null;
  barcode: string | null;
  price: unknown;
  compareAtPrice: unknown;
  inventoryQuantity: number | null;
  imageUrl: string | null;
}

export interface CatalogHealthRule {
  /// 🔴 VERİTABANINA yazılıyor — yeniden adlandırılamaz.
  code: string;
  /// Puan cezası.
  weight: number;
  severity: CatalogHealthSeverity;
  /// i18n anahtarı. Kural listesinde düz metin YOK.
  messageKey: string;
  /// `variant` = herhangi bir varyant ihlal ederse ürün ihlal etmiş sayılır.
  scope: 'product' | 'variant';
  /// AI düzeltme akışı bu maddeyi çözebilir mi. Yalnız METİN üretimiyle
  /// çözülebilenler `true`.
  fixableByAi: boolean;
  field: CatalogFixableField | null;
  /// 🔴 `true` = SORUN VAR.
  evaluate: (p: CatalogHealthProduct, vs: CatalogHealthVariant[]) => boolean;
}

const text = (s: string | null | undefined): string => (s ?? '').trim();
const num = (v: unknown): number => Number(v);

export const CATALOG_HEALTH_RULES: readonly CatalogHealthRule[] = [
  {
    code: 'NO_IMAGE',
    weight: 30,
    severity: 'critical',
    messageKey: 'catalog.health.rule.noImage',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // Varyant görseli koşulu, görselleri YALNIZ varyanta yazan pazaryeri
    // adapter'larında (Trendyol/İkas/Ticimax) kusursuz bir katalogun toptan
    // suçlanmasını engelliyor.
    evaluate: (p, vs) =>
      (p.imageCount ?? 0) === 0 && !text(p.imageUrl) && vs.every((v) => !text(v.imageUrl)),
  },
  {
    code: 'OUT_OF_STOCK',
    weight: 18,
    severity: 'critical',
    messageKey: 'catalog.health.rule.outOfStock',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // 🔴 `vs.length > 0` ZORUNLU: `[].every()` `true` döner ve guard olmasa
    // varyantı henüz senkronlanmamış HER ürün "tükendi" sayılırdı.
    // `=== 0` bilinçli: `null` "stok takibi yok" demek (dijital ürün) ve
    // negatif değer Shopify'da "stok bitse de sat" açıkken oluşuyor — yani
    // sayfa satın alınabilir durumda.
    evaluate: (_p, vs) => vs.length > 0 && vs.every((v) => v.inventoryQuantity === 0),
  },
  {
    code: 'DESCRIPTION_MISSING',
    weight: 15,
    severity: 'critical',
    messageKey: 'catalog.health.rule.descriptionMissing',
    scope: 'product',
    fixableByAi: true,
    field: 'description',
    // Görünmez biçim karakterleri (ZWSP/ZWNJ/ZWJ/word-joiner/BOM/yumuşak tire)
    // atılıyor, sonra en az bir harf veya rakam aranıyor. Düz `.trim()`,
    // zengin metin editöründen gelen "görsel olarak boş" açıklamaları sağlıklı
    // sayıyordu. `\p{L}` sayesinde Türkçe/Arapça/CJK metin tetiklemiyor.
    evaluate: (p) =>
      !/[\p{L}\p{N}]/u.test((p.description ?? '').replace(/[​-‍⁠﻿­]/gu, '')),
  },
  {
    code: 'ZERO_PRICE',
    weight: 14,
    severity: 'critical',
    messageKey: 'catalog.health.rule.zeroPrice',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // HİÇBİR varyantta geçerli fiyat yoksa ihlal. `some` ile yazılan ilk hâli
    // ücretsiz deneme/numune varyantı olan sağlıklı ürünleri de vuruyordu.
    // `!(x > 0)` biçimi 0'ı, negatifi ve NaN'ı birlikte yakalıyor.
    evaluate: (_p, vs) => !vs.some((v) => num(v.price) > 0),
  },
  {
    code: 'INACTIVE_STATUS',
    weight: 12,
    severity: 'high',
    messageKey: 'catalog.health.rule.inactiveStatus',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // Pozitif karşılaştırma bilinçli: `!== 'ACTIVE'` yazılsaydı yarın enuma
    // eklenecek SATILABİLİR bir platform durumu sessizce ihlal sayılırdı.
    evaluate: (p) => p.status === 'DRAFT' || p.status === 'ARCHIVED',
  },
  {
    code: 'FEED_LINK_MISSING',
    weight: 10,
    severity: 'high',
    messageKey: 'catalog.health.rule.feedLinkMissing',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // Ölçülen şey "reklamın varış noktası var mı". `handle` yedek kabul
    // ediliyor: feed derleyicisi mağaza alan adı + slug ile adres üretebilir.
    // `ACTIVE` kapısı, DRAFT/ARCHIVED üründe `INACTIVE_STATUS` ile ÇİFT ceza
    // kesilmesini engelliyor.
    evaluate: (p) => p.status === 'ACTIVE' && !text(p.onlineStoreUrl) && !text(p.handle),
  },
  {
    code: 'FEED_IDENTIFIER_MISSING',
    weight: 9,
    severity: 'high',
    messageKey: 'catalog.health.rule.feedIdentifierMissing',
    scope: 'variant',
    fixableByAi: false,
    field: null,
    // 🔴 Yalnız SKU'ya bakan ilk hâli, kimliği BARKOD üzerinden yürüyen
    // katalogları (Trendyol'da barkod zorunlu birincil kimlik) toptan ihlalli
    // işaretliyordu. Ürün↔reklam köprüsünün yedek eşleme anahtarı da bu.
    evaluate: (_p, vs) => vs.some((v) => !text(v.sku) && !text(v.barcode)),
  },
  {
    code: 'MISSING_PRODUCT_TYPE',
    weight: 7,
    severity: 'high',
    messageKey: 'catalog.health.rule.missingProductType',
    scope: 'product',
    fixableByAi: true,
    field: 'productType',
    // Boş, yalnız-boşluk ve yer tutucu değerler ("1042", "-", "N/A") tek
    // maddede: üçü de taksonomi eşleştiricisine aynı şeyi söylüyor — hiçbir şey.
    // `typeof` kalkanı, kategori kimliğini SAYI olarak yazan bir adapter'da
    // `.trim()` çökmesini önlüyor.
    evaluate: (p) =>
      !/\p{L}{2,}/u.test(typeof p.productType === 'string' ? p.productType.trim() : ''),
  },
  {
    code: 'SINGLE_IMAGE',
    weight: 7,
    severity: 'medium',
    messageKey: 'catalog.health.rule.singleImage',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // Tek görselde ürün içi kreatif rotasyonu ve açı testi yapılamıyor.
    // `NO_IMAGE`in dörtte biri: orada "kreatif ÜRETİLEMEZ", burada yalnız
    // "kreatif ÇEŞİTLENEMEZ".
    evaluate: (p) => p.imageCount === 1,
  },
  {
    code: 'TITLE_TOO_SHORT',
    weight: 6,
    severity: 'medium',
    messageKey: 'catalog.health.rule.titleTooShort',
    scope: 'product',
    fixableByAi: true,
    field: 'title',
    evaluate: (p) => {
      const t = p.title.trim();
      const tokens = t.split(/\s+/).filter(Boolean);
      // Tek jeton: "kirmizi-pamuklu-tisort-erkek" gibi handle'dan gelmiş
      // başlıklar UZUN olsa da eksiktir (CSV/pazaryeri göçlerinin klasiği).
      if (tokens.length < 2) return true;
      // Marka başlıktaysa "Beymen Cüzdan" = marka + tür; asgari feed başlığı
      // zaten sağlanmış.
      const brand = text(p.vendor).toLowerCase();
      const hasBrand = brand.length >= 2 && t.toLowerCase().includes(brand);
      // Alfanümerik model kodu ("WH-1000XM5", "V15") başlığı aramada tam
      // eşleşir kılıyor.
      const hasModelCode = /[\p{L}][\p{N}]|[\p{N}][\p{L}]/u.test(t);
      if (hasBrand || hasModelCode) return false;
      return t.length < 25 && tokens.length < 3;
    },
  },
  {
    code: 'TITLE_SPAM_SIGNALS',
    weight: 6,
    severity: 'high',
    messageKey: 'catalog.health.rule.titleSpamSignals',
    scope: 'product',
    fixableByAi: true,
    field: 'title',
    evaluate: (p) => {
      const t = text(p.title);
      if (!t) return false;
      // 1) Aşırı büyük harf. Oran TÜM harfler üzerinden değil, yalnız "sözcük"
      //    jetonları üzerinden: rakam içeren (X470, 18V-50) ve 5 harften kısa
      //    (USB, HDMI, XL, NIKE) jetonlar havuza girmiyor. En az 2 böyle sözcük
      //    gerekiyor ki tek kelimelik marka adı tetiklemesin.
      const tokens = t.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
      const words = tokens.filter(
        (w) => !/\p{N}/u.test(w) && (w.match(/\p{L}/gu) ?? []).length >= 5,
      );
      const pool = words.join('');
      const poolLetters = (pool.match(/\p{L}/gu) ?? []).length;
      // 🔴 `\p{Lu}` kullanılıyor, `toLocaleUpperCase` DEĞİL: Türkçe I/İ
      // dönüşümü yanlış eşleşme üretiyor. Kasasız yazılarda (Arapça, CJK)
      // `poolUpper` 0 kalıyor — yani kural o dillerde asla tetiklemiyor.
      const poolUpper = (pool.match(/\p{Lu}/gu) ?? []).length;
      const shouting = words.length >= 2 && poolLetters >= 10 && poolUpper / poolLetters > 0.7;
      // 2) Promosyon noktalaması. Tekrarlı işaret tek başına yeter; dağınık tek
      //    işaretlerde eşik 3, çünkü kitap/film adlarında iki ünlem dürüstçe
      //    geçebiliyor.
      const bangs = (t.match(/[!?！？‼⁉❗❕]/gu) ?? []).length;
      const promoPunct = /[!?！？‼⁉❗❕]{2,}/u.test(t) || bangs >= 3;
      // 3) Süs sembolü. 🔴 `u` bayrağı ŞART: bayraksız sınıfta tek bir 🔥 iki
      //    UTF-16 vekil birimine ayrıldığı için `{2,}` eşiğini KENDİ BAŞINA
      //    geçiyordu — yani tek emojili dürüst bir başlık spam sayılıyordu.
      const decor = (t.match(/[★☆✦✧✪✰⭐✨🔥💥💯🎁🎉➤➔]/gu) ?? []).length;
      return shouting || promoPunct || decor >= 2;
    },
  },
  {
    code: 'TITLE_TOO_LONG',
    weight: 5,
    severity: 'medium',
    messageKey: 'catalog.health.rule.titleTooLong',
    scope: 'product',
    fixableByAi: true,
    field: 'title',
    // `.length` UTF-16 birimi sayıyor; emoji taşıyan başlıklarda gerçek
    // karakter sayısından fazla çıkıyor. `Array.from` kod noktası sayıyor.
    evaluate: (p) => Array.from(p.title.replace(/\s+/g, ' ').trim()).length > 150,
  },
  {
    code: 'MISSING_VENDOR',
    weight: 5,
    severity: 'medium',
    messageKey: 'catalog.health.rule.missingVendor',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // 🔴 BARKOD KOŞULU ZORUNLU: kural yalnız ürünün markalı/seri üretim
    // olduğuna dair OLUMLU kanıt varken tetikliyor. El yapımı, hizmet ve
    // dijital üründe barkod yok — ceza da almıyorlar.
    evaluate: (p, vs) => {
      const v = text(p.vendor);
      const placeholder =
        /^(?:[-–—_./\\]+|n\/a|n\.a\.|yok|marka\s*yok|markas[ıi]z|bilinmiyor|belirtilmemi[şs]|unknown|none|null|undefined|no\s*brand|generic|jenerik)$/i.test(
          v,
        );
      return (v.length === 0 || placeholder) && vs.some((x) => text(x.barcode).length > 0);
    },
  },
  {
    code: 'FEED_SKU_DUPLICATE',
    weight: 5,
    severity: 'medium',
    messageKey: 'catalog.health.rule.feedSkuDuplicate',
    scope: 'product',
    fixableByAi: false,
    field: null,
    // 🔴 Bu madde ürün↔reklam köprüsünü DOĞRUDAN etkiliyor: çift SKU, SKU
    // basamağını belirsiz kılıyor ve o satırlar `UNMATCHED` kalıyor
    // (`buildProductMatchIndex` → `ambiguousSkus`).
    evaluate: (_p, vs) => {
      const norm = (s: string | null): string => text(s).toLowerCase();
      const ks = vs.map((v) => norm(v.sku)).filter((s) => s.length > 0);
      if (new Set(ks).size >= ks.length) return false;
      // Her varyantın dolu ve BİRBİRİNDEN FARKLI barkodu varsa kalem kimliği
      // zaten çözülebiliyor: model kodunu tüm varyantlara aynı yazan pazaryeri
      // kataloglarında kural susuyor.
      const bs = vs.map((v) => norm(v.barcode)).filter((s) => s.length > 0);
      const identityFromBarcode = bs.length === vs.length && new Set(bs).size === bs.length;
      return !identityFromBarcode;
    },
  },
  {
    code: 'VARIANT_IMAGE_MISSING',
    weight: 4,
    severity: 'medium',
    messageKey: 'catalog.health.rule.variantImageMissing',
    scope: 'variant',
    fixableByAi: false,
    field: null,
    // Ön koşul (`some(...)`) satıcının varyant görseli KULLANDIĞINI kanıtlıyor.
    // Onsuz, S/M/L gibi görsel olarak ayrışmayan varyantlarla çalışan dürüst
    // mağazalar ve görseli ürün düzeyinde tutan pazaryerleri toptan cezalanırdı.
    evaluate: (_p, vs) =>
      vs.length > 1 && vs.some((v) => Boolean(text(v.imageUrl))) && vs.some((v) => !text(v.imageUrl)),
  },
  {
    code: 'MISLEADING_COMPARE_AT_PRICE',
    weight: 4,
    severity: 'high',
    messageKey: 'catalog.health.rule.misleadingCompareAtPrice',
    scope: 'variant',
    fixableByAi: false,
    field: null,
    // Eşitlik dalı KALDIRILDI: `compareAtPrice === price` hiçbir vitrinde üstü
    // çizili fiyat basmıyor ve liste fiyatı tabanlı platformlarda indirimsiz
    // ürünün NORMAL hâli. `> 0` koşulu "yok" yerine 0 yazan adapter'ları eliyor.
    evaluate: (_p, vs) =>
      vs.some(
        (v) =>
          v.compareAtPrice !== null &&
          num(v.compareAtPrice) > 0 &&
          num(v.compareAtPrice) < num(v.price),
      ),
  },
  {
    code: 'NO_SEGMENTATION_AXIS',
    weight: 4,
    severity: 'low',
    messageKey: 'catalog.health.rule.noSegmentationAxis',
    scope: 'product',
    fixableByAi: true,
    field: 'tags',
    // Sayılan şey etiket DEĞİL, SEGMENTLENEBİLİRLİK. Ürün seti kurgusu etiket,
    // kategori ve marka eksenlerinin herhangi İKİSİYLE yürüyor; etiket kavramı
    // olmayan pazaryerlerinde kategori + marka dolu olduğu için kural susuyor.
    evaluate: (p) => {
      const norm = (s: string | null): string => text(s).toLowerCase();
      const tags = new Set(p.tags.map(norm).filter((t) => t.length > 0));
      const dims = tags.size + (norm(p.productType) ? 1 : 0) + (norm(p.vendor) ? 1 : 0);
      return dims < 2;
    },
  },
  {
    code: 'DESCRIPTION_TOO_SHORT',
    weight: 3,
    severity: 'low',
    messageKey: 'catalog.health.rule.descriptionTooShort',
    scope: 'product',
    fixableByAi: true,
    field: 'description',
    evaluate: (p) => {
      const d = (p.description ?? '').replace(/\s+/g, ' ').trim();
      // Boş olan `DESCRIPTION_MISSING`in işi — aynı ürün İKİ KEZ cezalanmıyor.
      if (d.length === 0) return false;
      const words = d.split(' ').filter((w) => /[\p{L}\p{N}]/u.test(w));
      // <80 karakter: bu bir açıklama değil ürün etiketi. <6 kelime: URL/hash
      // dolgusuyla eşiği aşma girişimi. 🔴 Eşik 150'den 80'e ÇEKİLDİ: 150 ne
      // Merchant Center'ın ne Meta'nın şartı ve dürüst kısa açıklamaları
      // toptan vuruyordu.
      return d.length < 80 || words.length < 6;
    },
  },
];

/// Bütün ağırlıkların toplamı (164). 100'den büyük olması bilinçli — dosya
/// başlığındaki puan gerekçesine bak.
export const CATALOG_HEALTH_TOTAL_WEIGHT: number = CATALOG_HEALTH_RULES.reduce(
  (sum, rule) => sum + rule.weight,
  0,
);

/// AI düzeltmesinin çözebileceği maddeler — `agent` alanının öneri akışı
/// yalnız bunları hedefliyor.
export const AI_FIXABLE_RULES: readonly CatalogHealthRule[] = CATALOG_HEALTH_RULES.filter(
  (rule) => rule.fixableByAi,
);

export interface CatalogHealthResult {
  /// 0–100.
  score: number;
  /// İhlal edilen kural kodları, ağırlığa göre AZALAN sıralı — arayüz ilk
  /// maddeyi "en önemli sorun" diye gösterebilsin.
  issues: string[];
  /// AI'ın düzeltebileceği alanlar (tekilleştirilmiş).
  fixableFields: CatalogFixableField[];
}

/**
 * Bir ürünün sağlık puanı.
 *
 * ⚠️ SAF fonksiyon: ağ yok, veritabanı yok, tarih yok. Bu, aynı girdinin her
 * yerde aynı puanı vermesini garanti ediyor — worker'ın yazdığı puanla API'nin
 * gösterdiği puan ayrışamaz.
 */
export function evaluateCatalogHealth(
  product: CatalogHealthProduct,
  variants: CatalogHealthVariant[],
): CatalogHealthResult {
  const violated = CATALOG_HEALTH_RULES.filter((rule) => rule.evaluate(product, variants));
  const penalty = violated.reduce((sum, rule) => sum + rule.weight, 0);

  const fields = new Set<CatalogFixableField>();
  for (const rule of violated) {
    if (rule.fixableByAi && rule.field) fields.add(rule.field);
  }

  return {
    score: Math.max(0, 100 - penalty),
    issues: [...violated].sort((a, b) => b.weight - a.weight).map((rule) => rule.code),
    fixableFields: [...fields],
  };
}

/// Kod → kural. Arayüz `healthIssues` kolonundaki kodu çeviri anahtarına ve
/// ağırlığa çevirmek için kullanıyor.
export const CATALOG_HEALTH_RULE_BY_CODE: ReadonlyMap<string, CatalogHealthRule> = new Map(
  CATALOG_HEALTH_RULES.map((rule) => [rule.code, rule]),
);
